import React, { useEffect, useState, useMemo } from "react";
import { db } from "../../firebase";
import logoImg from "../../assets/logo.png";
import {
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    n || 0
  );

export default function MyTimesheetsPayslips() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timesheets, setTimesheets] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("latest");

  // ──────────────────────────────────────────────
  // Fetch logged-in staff timesheets and payslips
  // ──────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      try {
        setLoading(true);

        // Find staffId
        const staffSnap = await getDocs(
          query(collection(db, "staff"), where("email", "==", user.email.toLowerCase()))
        );
        if (staffSnap.empty) {
          console.warn("No staff record found for:", user.email);
          setLoading(false);
          return;
        }
        const staffId = staffSnap.docs[0].id;

        // Fetch timesheets
        const tsSnap = await getDocs(
          query(
            collection(db, "timesheets"),
            where("staffId", "==", staffId),
            where("status", "==", "Approved")
          )
        );
        const tsData = tsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        // Fetch payslips
        const psSnap = await getDocs(
          query(collection(db, "payslips"), where("staffId", "==", staffId))
        );
        const psData = psSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        tsData.sort((a, b) => String(b.weekStart).localeCompare(String(a.weekStart)));
        psData.sort((a, b) => String(b.weekStart).localeCompare(String(a.weekStart)));

        setTimesheets(tsData);
        setPayslips(psData);
        if (tsData.length && selectedWeek === "latest") {
          setSelectedWeek(tsData[0].weekStart);
        }
      } catch (err) {
        console.error("Error fetching staff data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  const weeks = useMemo(() => {
    const set = new Set(timesheets.map((t) => t.weekStart));
    return Array.from(set).sort((a, b) => String(b).localeCompare(String(a)));
  }, [timesheets]);

  const visibleTimesheets = useMemo(() => {
    if (selectedWeek === "latest" && weeks.length)
      return timesheets.filter((t) => t.weekStart === weeks[0]);
    return timesheets.filter((t) => t.weekStart === selectedWeek);
  }, [timesheets, selectedWeek, weeks]);

  const visiblePayslips = useMemo(() => {
    if (selectedWeek === "latest" && weeks.length)
      return payslips.filter((p) => p.weekStart === weeks[0]);
    return payslips.filter((p) => p.weekStart === selectedWeek);
  }, [payslips, selectedWeek, weeks]);

  // ──────────────────────────────────────────────
  // PDF Export Utilities
  // ──────────────────────────────────────────────
  const exportElementToPDF = async (elementId, filename) => {
    const el = document.getElementById(elementId);
    if (!el) {
      alert("⚠️ Element not found for export.");
      return;
    }

    const pdf = new jsPDF("p", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const headerY = 30;

    // Header
    const logo = new Image();
    logo.src = logoImg;
    pdf.addImage(logo, "PNG", 40, headerY, 50, 50);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.text("Convalesce Care & Recruitment", 100, headerY + 20);
    pdf.setFontSize(10);
    pdf.text("Professional & Expert Touch", 100, headerY + 35);

    pdf.setDrawColor(200);
    pdf.line(40, headerY + 60, pageWidth - 40, headerY + 60);

    // Capture content
    const canvas = await html2canvas(el, { scale: 2, backgroundColor: "#ffffff" });
    const imgData = canvas.toDataURL("image/png");
    const imgWidth = pageWidth - 80;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let position = headerY + 80;
    pdf.addImage(imgData, "PNG", 40, position, imgWidth, imgHeight);
    pdf.save(filename);
  };

  const exportAllTimesheetsPDF = () =>
    exportElementToPDF("timesheet-list", "My_Timesheets.pdf");

  const exportAllPayslipsPDF = () =>
    exportElementToPDF("payslip-list", "My_Payslips.pdf");

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen text-gray-200 bg-gray-700">
        Loading your data...
      </div>
    );

  return (
    <div
      style={{
        paddingTop: "30px",
        
      }}
    >
      <h1
        style={{
          textAlign: "center",
          color: "gray",
          backgroundColor: "white",
          padding: "14px 28px",
          borderRadius: "20px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
          width: "fit-content",
          margin: "0 auto",
          fontSize: "16px",
          fontWeight: "500",
        }}
      >
        My Timesheets & Payslips
      </h1>

      {/* Week Selector */}
      <div
        style={{
          maxWidth: "900px",
          margin: "10px auto 0 auto",
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "20px 24px",
          boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
          color: "#1f2937",
        }}
      >
        <label
          htmlFor="weekSelect"
          style={{
            display: "block",
            fontSize: "14px",
            color: "#6b7280",
            marginBottom: "8px",
            fontWeight: "500",
          }}
        >
          Select Week
        </label>

        <select
          id="weekSelect"
          value={selectedWeek}
          onChange={(e) => setSelectedWeek(e.target.value)}
          style={{
            width: "100%",
            maxWidth: "300px",
            padding: "10px 12px",
            borderRadius: "10px",
            border: "1px solid #d1d5db",
            fontSize: "14px",
            color: "#1f2937",
            backgroundColor: "#f9fafb",
            outline: "none",
          }}
        >
          <option value="latest">Latest</option>
          {weeks.map((w) => (
            <option key={w} value={w}>
              {w}
            </option>
          ))}
        </select>
		
		<p style={{ fontSize: "13px", color: "#6b7280", marginTop: "8px" }}>
  Only weeks with approved timesheets are listed below.
</p>

      </div>

      {/* Timesheets */}
      <div
        id="timesheet-list"
        style={{
          maxWidth: "900px",
          margin: "40px auto",
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "24px",
          color: "#1f2937",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <h2 style={{ fontSize: "20px", fontWeight: "600" }}>
            Timesheets — Week {selectedWeek === "latest" ? weeks[0] : selectedWeek}
          </h2>
          <button
            onClick={exportAllTimesheetsPDF}
            style={{
              backgroundColor: "#374151",
              color: "white",
              padding: "10px 16px",
              borderRadius: "8px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            📄 Export All Timesheets PDF
          </button>
        </div>

        {visibleTimesheets.length === 0 ? (
          <p style={{ color: "gray" }}>No approved timesheets for this week.</p>
        ) : (
          visibleTimesheets.map((ts) => (
            <div
              key={ts.id}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "16px",
                padding: "24px",
                marginBottom: "24px",
                backgroundColor: "#f9fafb",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  borderBottom: "1px solid #e5e7eb",
                  paddingBottom: "8px",
                  marginBottom: "12px",
                }}
              >
                <div>
                  <h3 style={{ fontSize: "18px", fontWeight: "600" }}>{ts.staffName}</h3>
                  <p style={{ fontSize: "13px", color: "#6b7280" }}>
                    {ts.weekStart} → {ts.weekEnd} • Status:{" "}
                    <span style={{ color: "#16a34a", fontWeight: "500" }}>
                      {ts.status}
                    </span>
                  </p>
                </div>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    fontSize: "13px",
                    borderCollapse: "collapse",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor: "#e5e7eb",
                        textAlign: "left",
                        color: "#374151",
                      }}
                    >
                      <th style={{ padding: "8px" }}>Date</th>
                      <th style={{ padding: "8px" }}>Client</th>
                      <th style={{ padding: "8px", textAlign: "right" }}>Hours</th>
                      <th style={{ padding: "8px", textAlign: "right" }}>Rate</th>
                      <th style={{ padding: "8px", textAlign: "right" }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ts.entries?.map((e, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: "1px solid #e5e7eb",
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f3f4f6",
                        }}
                      >
                        <td style={{ padding: "8px" }}>{e.date}</td>
                        <td style={{ padding: "8px" }}>{e.clientName}</td>
                        <td style={{ padding: "8px", textAlign: "right" }}>{e.hours}</td>
                        <td style={{ padding: "8px", textAlign: "right" }}>
                          {fmtGBP(e.rate)}
                        </td>
                        <td style={{ padding: "8px", textAlign: "right" }}>
                          {fmtGBP(e.hours * e.rate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: "600" }}>
                      <td style={{ padding: "8px" }} colSpan={4}>
                        Total Hours
                      </td>
                      <td style={{ padding: "8px", textAlign: "right" }}>
                        {fmtGBP(ts.totalPay)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <button
                onClick={() =>
                  exportElementToPDF(
                    `timesheet-${ts.id}`,
                    `Timesheet_${ts.staffName}_${ts.weekStart}.pdf`
                  )
                }
                style={{
                  backgroundColor: "#1e3a8a",
                  color: "white",
                  padding: "10px 16px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  marginTop: "12px",
                  cursor: "pointer",
                }}
              >
                ⬇️ Export This Timesheet
              </button>
            </div>
          ))
        )}
      </div>

      {/* Payslips */}
      <div
        id="payslip-list"
        style={{
          maxWidth: "900px",
          margin: "40px auto",
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "24px",
          color: "#1f2937",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <h2 style={{ fontSize: "20px", fontWeight: "600" }}>
            Payslips — Week {selectedWeek === "latest" ? weeks[0] : selectedWeek}
          </h2>
          <button
            onClick={exportAllPayslipsPDF}
            style={{
              backgroundColor: "#374151",
              color: "white",
              padding: "10px 16px",
              borderRadius: "8px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            📄 Export All Payslips PDF
          </button>
        </div>

        {visiblePayslips.length === 0 ? (
          <p style={{ color: "gray" }}>No payslips available for this week.</p>
        ) : (
          visiblePayslips.map((p) => (
            <div
              key={p.id}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "16px",
                padding: "24px",
                marginBottom: "24px",
                backgroundColor: "#f9fafb",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #e5e7eb",
                  marginBottom: "8px",
                }}
              >
                <h3 style={{ fontSize: "18px", fontWeight: "600" }}>{p.staffName}</h3>
                <p style={{ color: "#16a34a", fontWeight: "600" }}>
                  {fmtGBP(p.netPay)}
                </p>
              </div>

              <p style={{ fontSize: "13px", color: "#6b7280" }}>
                Week {p.weekStart} → {p.weekEnd} • Code {p.taxCode}
              </p>

              <div
                style={{
                  marginTop: "8px",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: "10px",
                }}
              >
                <div style={{ background: "#fff", padding: "8px", borderRadius: "8px" }}>
                  <p style={{ fontSize: "12px", color: "#6b7280" }}>Gross Pay</p>
                  <p style={{ fontWeight: "600" }}>{fmtGBP(p.grossPay)}</p>
                </div>
                <div style={{ background: "#fff", padding: "8px", borderRadius: "8px" }}>
                  <p style={{ fontSize: "12px", color: "#6b7280" }}>Income Tax</p>
                  <p style={{ fontWeight: "600" }}>{fmtGBP(p.incomeTax)}</p>
                </div>
                <div style={{ background: "#fff", padding: "8px", borderRadius: "8px" }}>
                  <p style={{ fontSize: "12px", color: "#6b7280" }}>Employee NI</p>
                  <p style={{ fontWeight: "600" }}>{fmtGBP(p.employeeNI)}</p>
                </div>
              </div>

              <button
                onClick={() =>
                  exportElementToPDF(
                    `payslip-${p.id}`,
                    `Payslip_${p.staffName}_${p.weekStart}.pdf`
                  )
                }
                style={{
                  backgroundColor: "#1e3a8a",
                  color: "white",
                  padding: "10px 16px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  marginTop: "12px",
                  cursor: "pointer",
                }}
              >
                ⬇️ Export This Payslip
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
